import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowRight, Bell } from 'lucide-react';
import { Popover } from '@radix-ui/themes';
import { Skeleton } from '../ui/skeleton';
import { NotificationList } from '../notifications/NotificationList';
import { useMarkNotificationsSeen, useNotifications } from '../../hooks/useNotifications';
import { apiErrorMessage } from '../../utils/errorMessage';

/**
 * The bell — **one bell**, not the mock-up's two.
 *
 * The mock-up carries a decorative bell with a static dot beside a wired `triangle-alert` panel:
 * two buttons for one thing, and a dot that means nothing. This keeps the bell's icon, which is
 * where everyone looks for notifications, and the second one's behaviour (D41).
 *
 * A **`Popover`, not a `DropdownMenu`** — and that is the a11y point, not a styling preference: a
 * menu announces itself as a list of `menuitem`s, and this panel holds none. Rendered in one, the
 * whole feed is invisible to Radix's roving focus and the panel announces as an empty menu. The
 * primitive comes from `@radix-ui/themes`, already a dependency and already used by this screen's
 * neighbours, and it brings the focus trap, the outside click and the Escape key the mock-up's
 * hand-rolled panel forgot.
 *
 * **The badge counts unread items, never pending requests** (D42). A count of state would go back
 * up on its own and never reach zero — which is what D28 accepted for the mobile segment, where
 * the number genuinely means "still to answer", and what a bell must not do.
 */
const NotificationBell = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const { data, isLoading, error } = useNotifications();
  const markSeen = useMarkNotificationsSeen();

  /**
   * Opening marks everything read. In an effect on `open` rather than in the click handler, so a
   * panel opened by the keyboard counts as read too.
   *
   * Only when something *is* unread: a `PATCH` on every open would move the watermark of someone
   * who is only glancing, and cost a write per visit.
   */
  useEffect(() => {
    if (open && (data?.unreadCount ?? 0) > 0 && !markSeen.isPending) markSeen.mutate();
    // `markSeen` is a stable mutation object; depending on it would re-fire on every state change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, data?.unreadCount]);

  const unread = data?.unreadCount ?? 0;

  return (
    <Popover.Root open={open} onOpenChange={setOpen}>
      <Popover.Trigger>
        <button
          type="button"
          className="relative flex h-[34px] w-[34px] items-center justify-center rounded-lg text-muted-foreground hover:bg-accent hover:text-foreground"
          // The count is in the name, not only in the dot: a coloured dot says "something", and a
          // screen reader gets nothing from it.
          aria-label={
            unread > 0
              ? t('{{count}} unread notification', { count: unread })
              : t('Notifications')
          }
        >
          <Bell className="h-[18px] w-[18px]" aria-hidden="true" />
          {unread > 0 && (
            <span
              className="absolute -right-0.5 -top-0.5 flex h-[17px] min-w-[17px] items-center justify-center rounded-[6px] border border-background bg-primary px-1 text-[10.5px] font-semibold text-primary-foreground"
              aria-hidden="true"
            >
              {unread > 99 ? '99+' : unread}
            </span>
          )}
        </button>
      </Popover.Trigger>

      <Popover.Content
        align="end"
        // The panel is chrome, not content: its own padding would fight the list's rows.
        className="w-[344px] overflow-hidden rounded-xl border bg-popover p-0 text-popover-foreground shadow-lg"
      >
        <div className="border-b px-4 py-[11px] text-[13px] font-semibold">{t('Notifications')}</div>

        <div className="max-h-[360px] overflow-y-auto">
          {isLoading ? (
            <div className="space-y-2 p-4">
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-10 w-full" />
            </div>
          ) : error ? (
            // Said, never rendered as an empty panel: "nothing happened" and "we could not ask"
            // are different facts (R-054).
            <p role="alert" className="px-4 py-6 text-center text-[13px] text-red-600">
              {apiErrorMessage(t, error)}
            </p>
          ) : (
            <NotificationList
              items={data?.items ?? []}
              emptyTitle={t('No notification yet')}
              onSelect={(item) => {
                setOpen(false);
                // To the follow-up, filtered on the mission, its drawer open (CA19).
                const key = encodeURIComponent(item.issueKey);
                navigate(
                  `/admin/intervention-requests?serviceDeskId=${item.serviceDeskId}&search=${key}&open=${key}`
                );
              }}
            />
          )}
        </div>

        <Link
          to="/admin/intervention-requests"
          onClick={() => setOpen(false)}
          className="flex items-center justify-center gap-1.5 border-t px-4 py-[11px] text-[12.5px] font-medium hover:bg-accent"
        >
          {t('See all intervention requests')}
          <ArrowRight className="h-[14px] w-[14px]" aria-hidden="true" />
        </Link>
      </Popover.Content>
    </Popover.Root>
  );
};

export default NotificationBell;
